import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { AllInitializationCookie, AuthCookie, CookieReadWriteService } from '../../services/authentication/cookie-read-write.service';
import { ShellContextFacade } from '../../services/shell-context/shell-context-facade.service';
import { AuthService, SessionInfo } from '../../services/authentication/auth.service';
import { EupRoutesService } from '@core/eupRoutes.service';
import { Consts } from '@shared/consts';
import { PostLoginService } from '../../services/authentication/post-login.service';
import { UrlTree } from '@angular/router';
import { AuthAngularService } from '@itero/auth-angular';
import { JwtDecodeWrapper } from '@core/app-initialization/jwt-decode.service';
import { TimberService } from '@logging/timber.service';

@Injectable({
	providedIn: 'root',
})
export class AppInitializationService {
	private cookieReadWriteService = inject(CookieReadWriteService);
	private shellContextFacade = inject(ShellContextFacade);
	private authService = inject(AuthService);
	private eupRoutesService = inject(EupRoutesService);
	private postLoginService = inject(PostLoginService);
	private authAngularService = inject(AuthAngularService);
	private readonly timberService = inject(TimberService);

	get allInitializationCookie(): AllInitializationCookie {
		return this.cookieReadWriteService.allInitializationCookie;
	}

	init(cookie: AllInitializationCookie): Observable<UrlTree | boolean> {
		if (cookie.missing.length > 0) {
			const hasAuthCookie = !!cookie.authCookie.accessToken && !!cookie.authCookie.sessionId;

			if (!hasAuthCookie) {
				return of(false);
			}

			// First-time platform login: auth is valid but business/infra cookies are missing.
			// Set up auth-angular with the token so API calls work, then allow navigation.
			this.initWithAuthCookie(cookie.authCookie);

			return of(true);
		}

		this.initWithAuthCookie(cookie.authCookie);

		// Upgrade session cookies to persistent so they survive browser close.
		this.upgradeCookiesToPersistent(cookie);

		if (this.isAuthenticated) {
			return of(true);
		}

		const decodedToken = JwtDecodeWrapper.decode(cookie.authCookie.accessToken);

		let email = '';

		if ('email' in decodedToken && typeof decodedToken.email === 'string') {
			email = decodedToken.email;
		} else if ('sub' in decodedToken && typeof decodedToken.sub === 'string') {
			email = decodedToken.sub;
		} else {
			return of(false);
		}

		return this.postLoginService.init(
			this.shellContextFacade.sessionInfo,
			cookie.infraCookie.region || '',
			cookie.authCookie.identityProvider,
			email
		);
	}

	/**
	 * Re-writes cookies with expiration so they survive browser restarts.
	 */
	private upgradeCookiesToPersistent(cookie: AllInitializationCookie): void {
		this.timberService.info('Upgrading cookies to persistent', { module: 'AppInitializationService' });

		if (cookie.authCookie.accessToken || cookie.authCookie.sessionId || cookie.authCookie.idToken) {
			this.cookieReadWriteService.writeAuthCookie({
				identityProvider: cookie.authCookie.identityProvider,
				accessToken: cookie.authCookie.accessToken,
				sessionId: cookie.authCookie.sessionId,
				idToken: cookie.authCookie.idToken,
			});
		}

		if (cookie.businessCookie.companyId) {
			this.cookieReadWriteService.writeBusinessCookie({
				companyId: cookie.businessCookie.companyId,
				companyType: cookie.businessCookie.companyType,
				impersonatedContactId: cookie.businessCookie.impersonatedContactId,
			});
		}

		if (cookie.infraCookie.region) {
			this.cookieReadWriteService.writeInfraCookie({ region: cookie.infraCookie.region });
		}
	}

	private initWithAuthCookie(cookie: AuthCookie) {
		// write to Auth-angular
		this.authAngularService.updateAuthInfo({
			accessToken: cookie.accessToken,
			sessionType: Consts.SessionType,
			sessionId: cookie.sessionId,
			authUrl: this.eupRoutesService.iTeroWebAuthApiUrl,
		});

		const sessionInfo: SessionInfo = {
			sessionId: cookie.sessionId,
			accessToken: cookie.accessToken,
			sessionType: Consts.SessionType,
			idToken: cookie.idToken,
		};

		// write to Local storage
		localStorage.setItem(Consts.Storage.SessionInfo, JSON.stringify(sessionInfo));

		// write to in-memory shell-context
		this.shellContextFacade.updateSessionInfo(sessionInfo);
	}

	private get isAuthenticated() {
		return this.authService.isAuthenticated();
	}
}
